/**
 * Shared display formatting for framework / ESG field values (sidebar + editor insert).
 */

/** Shown when a structured answer has no meaningful content (e.g. unit-only). */
export const EMPTY_DISPLAY = '—';

const isEmptyScalar = (v: unknown): boolean => {
  if (v === undefined || v === null) return true;
  if (typeof v === 'string') return v.trim().length === 0;

  return false;
};

const isValueUnitShape = (obj: Record<string, unknown>): boolean =>
  Object.prototype.hasOwnProperty.call(obj, 'value') ||
  Object.prototype.hasOwnProperty.call(obj, 'unit');

const formatValueUnitObject = (obj: Record<string, unknown>): string => {
  const numericOrTextValue = isEmptyScalar(obj.value) ? '' : String(obj.value);
  const unitValue = isEmptyScalar(obj.unit) ? '' : String(obj.unit);

  if (numericOrTextValue) {
    return [numericOrTextValue, unitValue].filter(Boolean).join(' ').trim();
  }

  if (unitValue) return EMPTY_DISPLAY;

  return '';
};

const isSelectLabelShape = (obj: Record<string, unknown>): boolean =>
  Object.prototype.hasOwnProperty.call(obj, 'label') &&
  !Object.prototype.hasOwnProperty.call(obj, 'unit');

const formatPlainObject = (obj: Record<string, unknown>): string => {
  if (isSelectLabelShape(obj)) {
    const label = obj.label;
    if (!isEmptyScalar(label)) return String(label);
    if (!isEmptyScalar(obj.value) && typeof obj.value !== 'object') {
      return String(obj.value);
    }

    return EMPTY_DISPLAY;
  }

  if (isValueUnitShape(obj)) {
    return formatValueUnitObject(obj);
  }

  if ('value' in obj) {
    if (!isEmptyScalar(obj.value) && typeof obj.value !== 'object') {
      return String(obj.value);
    }
    if (
      !isEmptyScalar(obj.value) &&
      typeof obj.value === 'object' &&
      obj.value !== null &&
      !Array.isArray(obj.value)
    ) {
      return formatPlainObject(obj.value as Record<string, unknown>);
    }

    return EMPTY_DISPLAY;
  }

  if (
    'value' in obj &&
    obj.value !== null &&
    typeof obj.value === 'object' &&
    !Array.isArray(obj.value)
  ) {
    return formatPlainObject(obj.value as Record<string, unknown>);
  }

  const displayKeys = ['text', 'name', 'label', 'title'] as const;
  for (const key of displayKeys) {
    if (key in obj && !isEmptyScalar(obj[key])) {
      return String(obj[key]);
    }
  }

  return EMPTY_DISPLAY;
};

const tryParseJsonString = (s: string): unknown | null => {
  const trimmed = s.trim();
  if (
    !(trimmed.startsWith('{') && trimmed.endsWith('}')) &&
    !(trimmed.startsWith('[') && trimmed.endsWith(']'))
  ) {
    return null;
  }
  try {
    return JSON.parse(trimmed) as unknown;
  } catch {
    return null;
  }
};

const formatUnknown = (value: unknown): string => {
  if (value === undefined || value === null) return '';

  if (typeof value === 'string') {
    const parsed = tryParseJsonString(value);
    if (parsed !== null) return formatUnknown(parsed);

    return value;
  }

  if (Array.isArray(value)) {
    return value
      .map((v) => formatUnknown(v))
      .filter((s) => s.length > 0)
      .join(', ');
  }

  if (typeof value === 'object') {
    return formatPlainObject(value as Record<string, unknown>);
  }

  return String(value);
};

export function formatFrameworkFieldValue(value: unknown): string {
  return formatUnknown(value);
}

/**
 * Fixed-row tables (`ADD_FIXED_ROW_TO_TABLE`) store answers under suffixed keys per row:
 * `${fieldName}_${rowId}` (see TableWithfixedRow). Plain tables use `fieldName` only.
 */
export function resolveFrameworkTableCell(
  row: Record<string, unknown>,
  colKey: string
): unknown {
  if (!colKey) return undefined;

  if (colKey === 'scopeLabel') {
    return row.scopeLabel ?? row.label;
  }

  if (Object.prototype.hasOwnProperty.call(row, colKey)) {
    return row[colKey];
  }

  const rawRowId = row.rowId ?? row._id ?? row.id;

  if (typeof rawRowId === 'string' || typeof rawRowId === 'number') {
    const suffixed = `${colKey}_${rawRowId}`;

    if (Object.prototype.hasOwnProperty.call(row, suffixed)) {
      return row[suffixed];
    }
  }

  return undefined;
}

const isPlainRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/**
 * Table answers may be stored as a row array, a JSON string of an array, or wrapped
 * in API envelopes like `{ value: rows[] }` (generic field shape — still used for tables).
 */
export function normalizeFrameworkTableValue(value: unknown): unknown {
  if (Array.isArray(value)) return value;

  if (isPlainRecord(value)) {
    const v = value.value;
    const r = value.rows;
    const d = value.data;
    if (Array.isArray(v)) return v;
    if (Array.isArray(r)) return r;
    if (Array.isArray(d)) return d;
    if (typeof v === 'string') {
      const inner = normalizeFrameworkTableValue(v);

      if (Array.isArray(inner)) return inner;
    }
  }

  if (typeof value !== 'string') return value;
  const t = value.trim();
  if (t.length === 0) return value;
  if (t.startsWith('[')) {
    try {
      const parsed = JSON.parse(t) as unknown;
      if (Array.isArray(parsed)) return parsed;
    } catch {
      return value;
    }
  }

  return value;
}
